/**
 * WorkspaceSavedFiles — per-department Saved Files browser.
 *
 * Lives inside every WorkspaceDialog. Shows every snapshot whose
 * `owner_node_id` is the current node OR any descendant (so visiting
 * "Spa" surfaces snapshots saved inside Spa, Spa → Inventories,
 * Spa → OS&E Lists, etc.).
 *
 * Capabilities:
 *   • Open / restore a snapshot — fires the same `open-snapshot` event
 *     the global SavedFilesDialog uses, so existing form-side listeners
 *     keep working (e.g. InventoryTable materialises restored rows
 *     as a new saved list).
 *   • Delete a snapshot (only the creator or an admin per backend RBAC).
 *   • Toggle Share / Private — controls cross-department visibility.
 *
 * RBAC: backend filters listings to "owned-by-me + shared + admin sees
 * all". This component only renders — it doesn't escalate visibility.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FolderOpen, FileSpreadsheet, FileText, Trash2, Share2, Lock, Loader2, ChevronDown, ChevronUp, Archive } from 'lucide-react';
import SnapshotViewerDialog from './SnapshotViewerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  inventory: 'Inventory',
  checklist: 'Checklist',
  permit: 'Permit',
  insurer: 'Insurer',
  subcontractor: 'Subcontractor',
};

export default function WorkspaceSavedFiles({ currentId, tree, defaultCollapsed = false }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [viewerSnap, setViewerSnap] = useState(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Compute every descendant node id of the current node so a snapshot
  // saved deep inside (e.g. spa.gallery) still surfaces at the Spa root.
  const scopedNodeIds = useMemo(() => {
    if (!currentId) return new Set();
    const set = new Set([currentId]);
    let added = true;
    while (added) {
      added = false;
      tree.forEach((n) => {
        if (n.parent_id && set.has(n.parent_id) && !set.has(n.id)) {
          set.add(n.id);
          added = true;
        }
      });
    }
    return set;
  }, [currentId, tree]);

  const load = async () => {
    if (!adminToken) {
      setSnapshots([]);
      return;
    }
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(`${API}/snapshots`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      // Filter to scoped node ids client-side. Backend filter accepts
      // exact match only, but a single fetch + JS filter is fine at
      // typical snapshot volumes (<500 per property).
      setSnapshots((data || []).filter((s) => scopedNodeIds.has(s.owner_node_id)));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load saved files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentId, adminToken]);

  const restore = (snap) => {
    // Click on a saved file now opens the universal viewer (with Print
    // and per-kind "Restore into form" affordances).
    setViewerSnap(snap);
  };

  const remove = async (snap) => {
    if (!window.confirm(`Permanently delete saved file "${snap.title}"? This cannot be undone — pick Archive instead if you might want it back.`)) return;
    try {
      await axios.delete(`${API}/admin/snapshots/${snap.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setSnapshots((s) => s.filter((x) => x.id !== snap.id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  /** Archive — soft-hides the snapshot. Admin can restore it from the
   *  Archive admin tab anytime. Skips the destructive confirm. */
  const archive = async (snap) => {
    try {
      await axios.put(`${API}/admin/archive/snapshot/${snap.id}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setSnapshots((s) => s.filter((x) => x.id !== snap.id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to archive');
    }
  };

  const toggleShare = async (snap) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/snapshots/${snap.id}`,
        { shared: !snap.shared },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setSnapshots((s) => s.map((x) => (x.id === snap.id ? data : x)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update share');
    }
  };

  return (
    <div data-testid="workspace-saved-files" className="rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2DBC8' }}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="workspace-saved-files-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        aria-expanded={!collapsed}
      >
        <FolderOpen className="w-4 h-4" style={{ color: '#15803D' }} />
        <h3 className="text-sm font-bold uppercase tracking-[0.15em]" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
          Saved Files
        </h3>
        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}>
          {snapshots.length}
        </span>
        <span className="ml-auto text-gray-400">
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
      {loading && (
        <div className="flex items-center justify-center py-6 text-gray-500 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && err && (
        <p className="text-xs text-red-600 py-2">{err}</p>
      )}

      {!loading && !err && snapshots.length === 0 && (
        <p className="text-xs text-gray-500 py-2" style={{ fontFamily: 'Manrope' }}>
          No saved files in this department yet. Open any form in this department, fill it out, and press <strong className="text-green-700">Save snapshot</strong> to keep a frozen copy here.
        </p>
      )}

      {!loading && !err && snapshots.length > 0 && (
        <ul className="space-y-1.5" data-testid="workspace-saved-files-list">
          {snapshots.map((s) => (
            <li
              key={s.id}
              data-testid={`workspace-saved-file-${s.id}`}
              className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 hover:bg-emerald-50 transition-colors"
            >
              <div className="flex-shrink-0 w-6 h-6 rounded bg-white border border-gray-200 flex items-center justify-center">
                {s.kind === 'inventory'
                  ? <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                  : <FileText className="w-3.5 h-3.5 text-blue-600" />}
              </div>
              <button
                type="button"
                onClick={() => restore(s)}
                className="flex-1 min-w-0 text-left"
                data-testid={`workspace-saved-file-open-${s.id}`}
                title="Open / restore this snapshot"
              >
                <p className="text-xs font-bold text-gray-900 truncate">{s.title}</p>
                <p className="text-[10px] text-gray-500 truncate">
                  {KIND_LABELS[s.kind] || s.kind} · {s.created_by_name} · {new Date(s.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                </p>
              </button>
              <button
                type="button"
                onClick={() => toggleShare(s)}
                className={`p-1 rounded transition-colors ${s.shared ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-200'}`}
                data-testid={`workspace-saved-file-share-${s.id}`}
                title={s.shared ? 'Shared with team — click to make private' : 'Private — click to share with this workspace'}
              >
                {s.shared ? <Share2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => archive(s)}
                className="p-1 rounded hover:bg-amber-100 text-amber-700"
                data-testid={`workspace-saved-file-archive-${s.id}`}
                title="Archive — soft-hides this saved file. Restore from Admin → Archive."
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => remove(s)}
                className="p-1 rounded hover:bg-red-100 text-red-600"
                data-testid={`workspace-saved-file-delete-${s.id}`}
                title="Permanently delete saved file"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
        </div>
      )}
      <SnapshotViewerDialog
        snapshot={viewerSnap}
        open={!!viewerSnap}
        onClose={() => setViewerSnap(null)}
      />
    </div>
  );
}
